import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  update_last_activity,
  mark_session_expired,
  logout,
  select_is_authenticated,
} from '../../store/auth_slice';
import type { AppDispatch } from '../../store';

/**
 * Session timeout handler component
 * Monitors user activity and handles session expiration
 */

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // 5 minutes warning
const CHECK_INTERVAL = 60 * 1000; // Check every minute

export const SessionTimeoutHandler = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const is_authenticated = useSelector(select_is_authenticated);
  const last_activity = useSelector((state: any) => state.auth.last_activity);
  
  const timeout_timer_ref = useRef<NodeJS.Timeout | null>(null);
  const check_timer_ref = useRef<NodeJS.Timeout | null>(null);
  const warning_shown_ref = useRef(false);

  /**
   * Update last activity timestamp
   */
  const handle_user_activity = useCallback(() => {
    if (is_authenticated) {
      dispatch(update_last_activity());
      warning_shown_ref.current = false;
    }
  }, [is_authenticated, dispatch]);

  /**
   * Handle session expiration
   */
  const handle_session_timeout = useCallback(() => {
    dispatch(mark_session_expired());
    dispatch(logout());
    navigate('/login?session=expired');
  }, [dispatch, navigate]);

  /**
   * Show warning before session expires
   */
  const show_timeout_warning = useCallback(() => {
    if (!warning_shown_ref.current) {
      warning_shown_ref.current = true;
      
      // You can implement a custom modal/toast here
      const should_continue = window.confirm(
        'Your session is about to expire in 5 minutes. Click OK to stay logged in.'
      );
      
      if (should_continue) {
        handle_user_activity();
      }
    }
  }, [handle_user_activity]);

  /**
   * Check session status
   */
  const check_session_status = useCallback(() => {
    if (!is_authenticated) return;

    const current_time = Date.now();
    const time_elapsed = current_time - last_activity;
    const time_remaining = SESSION_TIMEOUT - time_elapsed;

    // Session expired
    if (time_elapsed >= SESSION_TIMEOUT) {
      handle_session_timeout();
      return;
    }

    // Show warning
    if (time_remaining <= WARNING_BEFORE_TIMEOUT && !warning_shown_ref.current) {
      show_timeout_warning();
    }
  }, [is_authenticated, last_activity, handle_session_timeout, show_timeout_warning]);

  /**
   * Setup activity listeners
   */
  useEffect(() => {
    if (!is_authenticated) return;

    // Activity events to track
    const activity_events = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    activity_events.forEach((event) => {
      document.addEventListener(event, handle_user_activity);
    });

    // Start session check interval
    check_timer_ref.current = setInterval(check_session_status, CHECK_INTERVAL);

    // Cleanup
    return () => {
      activity_events.forEach((event) => {
        document.removeEventListener(event, handle_user_activity);
      });
      
      if (check_timer_ref.current) {
        clearInterval(check_timer_ref.current);
      }
      
      if (timeout_timer_ref.current) {
        clearTimeout(timeout_timer_ref.current);
      }
    };
  }, [is_authenticated, handle_user_activity, check_session_status]);

  /**
   * Handle visibility change (tab switch)
   */
  useEffect(() => {
    if (!is_authenticated) return;

    const handle_visibility_change = () => {
      if (document.visibilityState === 'visible') {
        check_session_status();
      }
    };

    document.addEventListener('visibilitychange', handle_visibility_change);

    return () => {
      document.removeEventListener('visibilitychange', handle_visibility_change);
    };
  }, [is_authenticated, check_session_status]);

  // This component doesn't render anything
  return null;
};